import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import {
    EventsManager,
    FlowEvents,
    GameEvents,
    ReelsEvents,
    WinEvents,
} from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { IBetWayWinData } from "../../../abstracts/types/interfaces";
import { getTotalFromArrayByPropertyName } from "../../../abstracts/utils/JSUtils";
import {
    fadeInBackgroundMusic,
    playNextBackgroundMusic,
    playSound,
} from "../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../Apocalypse";
import { ApocalypseSoundNames } from "../../assetsLoader/ApocalypseAssetsNames";
import { ApocalypseReelsView } from "../../modules/Reels/ApocalypseReelsView";
import ApocalypseReel from "../../modules/Reels/normal/ApocalypseReel";
import { ApocalypseSymbol } from "../../modules/Reels/symbols/ApocalypseSymbol";
import { ApocalypseWinView } from "../../modules/Winnings/ApocalypseWinView";
import {
    ApocalypseHighSymbolNames,
    ApocalypseSpecialSymbolNames,
} from "../../types/ApocalypseEnums";

export class ApocalypseNormalIdlePresentation extends AbstractFlowPresentation {
    private normalReels: ApocalypseReel[];
    private winView: ApocalypseWinView;
    private reelsView: ApocalypseReelsView;
    private idleCall: gsap.core.Tween;
    private restartIdleCall: gsap.core.Tween;

    constructor() {
        super(`ApocalypseNormalIdlePresentation`);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(FlowEvents.BET_STARTED, () => {
            this.killTimeline();

            this.winView.removeChild(this.winView.waysWinScreen);
            this.idleCall?.kill();
            this.restartIdleCall?.kill();
        });

        EventsManager.on(ReelsEvents.GET_NORMAL_REELS, (reels: ApocalypseReel[]) => {
            this.normalReels = reels;
        });

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (reelsView: ApocalypseReelsView) => {
            this.reelsView = reelsView;
        });

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (winView: ApocalypseWinView) => {
            this.winView = winView;
        });
    }

    public start(flowStateController: FlowStateController) {
        super.start(flowStateController);

        this.finishPresentation();

        const isJurisdictionAllowed: boolean =
            Apocalypse.apiPlugin.gameClientConfiguration.boostedBetAllowed;

        if (this.model.data === this.model.initData) {
            EventsManager.trigger(GameEvents.ENABLE_IDLE_ANIMATIONS, isJurisdictionAllowed);
        } else {
            this.idleCall = gsap.delayedCall(3 + Math.random() * 3, () => {
                EventsManager.trigger(GameEvents.ENABLE_IDLE_ANIMATIONS, isJurisdictionAllowed);
            });
        }

        if (!this.model.data.isRestore) {
            if (
                this.model.data.accumulatedRoundWin > 0 &&
                !Apocalypse.model.isWinBelowStake() &&
                this.model.data.mode === AbstractGameMode.NORMAL
            ) {
                this.generateWinningPresentation();
            }

            if (this.model.data.nextMode === AbstractGameMode.NORMAL) {
                playNextBackgroundMusic();

                if (this.model.data.mode !== AbstractGameMode.NORMAL) {
                    fadeInBackgroundMusic(5);
                }
            }
        }

        if (this.model.data.nextMode === AbstractGameMode.NORMAL) {
            this.reelsView.enableIdle();
        }
    }

    private generateWinningPresentation() {
        const isWinBelowStake: boolean =
            Apocalypse.apiPlugin.gameClientConfiguration.belowStakeWinRestriction &&
            this.model.data.accumulatedRoundWin <= this.model.data.playedBetValue;

        if (isWinBelowStake) {
            this.flowPresentationTimeline.resume();
            return;
        }

        this.flowPresentationTimeline = gsap.timeline({
            repeat: -1,
            delay: 1,
        });
        const winningsArray = Apocalypse.model.finalWinningPosition;

        if (this.model.data.betWayWins.length > 1) {
            // let hasAlreadyPlayedSound = false;

            this.model.data.betWayWins.forEach((betWayData: IBetWayWinData) => {
                let hasAlreadyPlayedSound = false;

                const wayTimeline = gsap.timeline({
                    smoothChildTiming: true,
                    autoRemoveChildren: false,
                    onStart: () => {
                        if (!hasAlreadyPlayedSound) {
                            if (betWayData.symbol === ApocalypseHighSymbolNames.M1) {
                                playSound({ name: ApocalypseSoundNames.WIN_SWEEP_M1 });
                            } else if (betWayData.symbol === ApocalypseHighSymbolNames.M2) {
                                playSound({ name: ApocalypseSoundNames.WIN_SWEEP_M2 });
                            } else if (betWayData.symbol === ApocalypseHighSymbolNames.M3) {
                                playSound({ name: ApocalypseSoundNames.WIN_SWEEP_M3 });
                            } else if (betWayData.symbol === ApocalypseHighSymbolNames.M4) {
                                playSound({ name: ApocalypseSoundNames.WIN_SWEEP_M4 });
                            } else if (betWayData.symbol === ApocalypseHighSymbolNames.M5) {
                                playSound({ name: ApocalypseSoundNames.WIN_SWEEP_M5 });
                            }

                            hasAlreadyPlayedSound = true;
                        }
                    },
                });

                this.normalReels.forEach((reel, reelIndex) => {
                    const visibleSymbols: AbstractSymbol[] = reel.getAllVisibleStopSymbols();
                    const reelWinSymbols = reel.getWinningSymbols(winningsArray[reelIndex]);
                    const wayWinSymbols: AbstractSymbol[] = [];

                    reelWinSymbols.forEach((symbol) => {
                        const cond1 = reelIndex <= betWayData.length - 1;
                        const cond2 = symbol.name === betWayData.symbol;

                        const cond3 = symbol.name === ApocalypseSpecialSymbolNames.XNUDGE;
                        const cond4 = symbol.name === ApocalypseSpecialSymbolNames.WILD;

                        if (
                            winningsArray[reelIndex][symbol.positionInArray - 1] &&
                            cond1 &&
                            (cond2 || cond3 || cond4)
                        ) {
                            const symbolWinAnimation = symbol.getWinAnimation();
                            wayTimeline.add(symbolWinAnimation, 0);
                            wayWinSymbols.push(symbol as ApocalypseSymbol);
                        }
                    });

                    visibleSymbols.forEach((symbol) => {
                        if (!wayWinSymbols.includes(symbol as ApocalypseSymbol)) {
                            wayTimeline.add((symbol as ApocalypseSymbol).getNoWinAnimation(), 0);
                        }
                    });
                });

                wayTimeline.add(
                    this.winView.waysWinScreen.handleWaysWin(
                        [],
                        [betWayData.amount * betWayData.multiplier],
                        betWayData.numberOfCombinations,
                        wayTimeline.totalDuration()
                    ),
                    0
                );

                this.flowPresentationTimeline.add(wayTimeline);
            });
        }

        //all ways handler
        let currentWinSweepCount = 0;
        if (this.model.data.betWayWins.length > 0) {
            // let winSweepCount = 0;
            let hasAlreadyPlayedSound = false;
            currentWinSweepCount++;

            const betWayData = this.model.data.betWayWins[currentWinSweepCount - 1];

            let soundName: string = ApocalypseSoundNames.WIN_SWEEP;

            if (this.model.data.betWayWins.length === 1) {
                if (betWayData.symbol === ApocalypseHighSymbolNames.M1) {
                    soundName = ApocalypseSoundNames.WIN_SWEEP_M1;
                } else if (betWayData.symbol === ApocalypseHighSymbolNames.M2) {
                    soundName = ApocalypseSoundNames.WIN_SWEEP_M2;
                } else if (betWayData.symbol === ApocalypseHighSymbolNames.M3) {
                    soundName = ApocalypseSoundNames.WIN_SWEEP_M3;
                } else if (betWayData.symbol === ApocalypseHighSymbolNames.M4) {
                    soundName = ApocalypseSoundNames.WIN_SWEEP_M4;
                } else if (betWayData.symbol === ApocalypseHighSymbolNames.M5) {
                    soundName = ApocalypseSoundNames.WIN_SWEEP_M5;
                }
            }

            const allWaysTimeline = gsap.timeline({
                onStart: () => {
                    if (!hasAlreadyPlayedSound) {
                        playSound({ name: soundName });
                        hasAlreadyPlayedSound = true;
                    }
                    // else {
                    // }
                    // if (this.model.data.betWayWins.length !== 1) {
                    // winSweepCount = this.model.data.betWayWins.length;
                    // }
                },
            });

            this.normalReels.forEach((reel, reelIndex) => {
                const winSymbols: AbstractSymbol[] = reel.getWinningSymbols(
                    winningsArray[reelIndex]
                );

                winSymbols.forEach((symbol) => {
                    allWaysTimeline.add(symbol.getWinAnimation(), 0);
                });

                const nonWinSymbols = reel.getNonWinningSymbols(winningsArray[reelIndex]);

                nonWinSymbols.forEach((symbol) => {
                    allWaysTimeline.add((symbol as ApocalypseSymbol).getNoWinAnimation(), 0);
                });
            });

            const totalWaysCount: number = getTotalFromArrayByPropertyName(
                this.model.data.betWayWins,
                `numberOfCombinations`
            );

            allWaysTimeline.add(
                this.winView.waysWinScreen.handleWaysWin(
                    [],
                    [this.model.data.accumulatedRoundWin],
                    totalWaysCount,
                    allWaysTimeline.totalDuration()
                ),
                0
            );

            this.flowPresentationTimeline.add(allWaysTimeline);
        }
    }
}
